"use client";

import { getImageProps } from "next/image";
import { motion } from "framer-motion";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";
import { GroqImage } from "@/helpers/Image";
import GradualBlur from "@/components/global/GradualBlur";
import TrustpilotRating from "../global/TrustpilotRating";
import { TrustpilotMicroStar } from "@/components/global/TrustpilotWidgets";
import TrustLogos from "./TrustLogos";

function CheckIcon() {
  return (
    <div className="flex h-[2.5rem] w-[2.5rem] flex-shrink-0 items-center justify-center bg-[#003828] lg:h-[3.375rem] lg:w-[3.375rem]">
      <svg
        viewBox="0 0 12 9"
        fill="none"
        className="h-[1rem] w-[1rem] lg:h-[1.125rem] lg:w-[1.5rem]">
        <path
          d="M1 4.5L4.5 8L11 1"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

const ease = [0.25, 0.1, 0.25, 1] as const;

export default function HeroSection({ data }: { data: PageHomeSchema }) {
  const {
    props: { src: bgSrc },
  } = getImageProps({
    src: data?.heroBackgroundImage?.src ?? "",
    width: data?.heroBackgroundImage?.width ?? 1920,
    height: data?.heroBackgroundImage?.height ?? 1176,
    alt: "",
    quality: 90,
  });

  return (
    <section className="relative flex min-h-[145rem] w-full flex-col items-center overflow-clip lg:min-h-[140rem]">
      {/* Decorative background image */}
      {data?.heroBackgroundImage && (
        <div
          className="pointer-events-none absolute inset-x-0 top-[22.5rem] h-[147rem]"
          aria-hidden="true">
          <motion.div
            initial={{ opacity: 0, filter: "blur(8px)" }}
            animate={{ opacity: 1, filter: "blur(0px)" }}
            transition={{ duration: 1.2, ease }}
            className="h-full w-full"
            style={{
              background: `linear-gradient(180deg, #FFF 0%, rgba(255, 255, 255, 0.00) 45%), linear-gradient(0deg, #E6EDEB 0%, rgba(230, 237, 235, 0.00) 14.19%), url(${bgSrc}) lightgray center top / cover no-repeat`,
            }}
          />
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 flex w-[45rem] flex-col items-center gap-[2.63rem] px-[0rem] pt-[3rem] text-center lg:w-[min(110rem,100%)] lg:gap-[3rem] lg:pt-[10rem]">
        <div className="flex flex-col items-center gap-[1.75rem] text-center">
          {data?.heroChip && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease, delay: 0.2 }}
              className="mb-0 font-blacker text-base leading-[1.5] text-black italic">
              {data.heroChip}
            </motion.p>
          )}

          {data?.heroTitle && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease, delay: 0.35 }}
              className="richtext flex flex-col items-center gap-[0.88rem] lg:w-full [&_h1]:[text-wrap:unset] [&_h2]:[text-wrap:unset] [&_h3]:[text-wrap:unset] prose-p:text-center prose-p:font-medium prose-headings:text-center prose-headings:text-[5.5rem] prose-headings:lg:text-[7rem]">
              {/* Desktop title */}
              <div className="hidden w-full flex-col gap-[2rem] lg:flex">
                <PortableText
                  value={data.heroTitle}
                  components={richTextComponent}
                />
              </div>
              {/* Mobile title — falls back to heroTitle if heroTitleMobile is empty */}
              <div className="flex w-full flex-col gap-[2rem] lg:hidden">
                <PortableText
                  value={data.heroTitleMobile ?? data.heroTitle}
                  components={richTextComponent}
                />
              </div>
            </motion.div>
          )}
        </div>

        {data?.heroChecklist && data.heroChecklist.length > 0 && (
          <motion.ul
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.45 }}
            className="mb-0 flex w-full list-none flex-col items-stretch justify-center gap-x-[2.625rem] gap-y-[0.75rem] p-0 lg:w-auto lg:flex-row lg:flex-wrap lg:items-center lg:gap-y-[1.5rem]">
            {data.heroChecklist.map((item, index) => (
              <li
                key={`${item}-${index}`}
                className="flex w-full items-center gap-x-[2.625rem] lg:w-auto">
                <div className="flex h-[6.25rem] w-full items-center gap-[1.625rem] border border-[rgba(0,0,0,0.1)] bg-white p-[1.75rem] lg:w-auto lg:border-0">
                  <div className="flex h-[3.375rem] w-[3.375rem] shrink-0 items-center justify-center bg-[#003828]">
                    <svg
                      viewBox="0 0 12 9"
                      fill="none"
                      className="h-[1.125rem] w-[1.5rem] shrink-0">
                      <path
                        d="M1 4.5L4.5 8L11 1"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span className="mb-0 text-center font-geist-mono text-[1.625rem] leading-[1.33] font-semibold whitespace-nowrap text-black uppercase">
                    {item}
                  </span>
                </div>
                {index < data.heroChecklist!.length - 1 && (
                  <span
                    aria-hidden="true"
                    className="hidden h-[4.5625rem] w-px bg-black/20 lg:block"
                  />
                )}
              </li>
            ))}
          </motion.ul>
        )}

        {data?.heroCTA && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.5 }}
            className="w-[44rem] lg:w-fit">
            <CTAButton data={data.heroCTA} />
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.65 }}
          className={`${data?.settings?.language === "de" ? "lg:w-[60rem] w-[44rem]" : "lg:w-[50rem] w-[44rem]"}`}>
          <TrustpilotMicroStar locale={data?.settings?.language === "de" ? "de-DE" : "en-US"} />
        </motion.div>

        {/* <motion.div
          className="mt-[2rem]"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease, delay: 0.35 }}
        >
          <PartnersBar
            partnerText={data?.introPartnerText}
            partnerLogo1={data?.introPartnerLogo1}
            partnerLogo2={data?.introPartnerLogo2}
          />
        </motion.div> */}
      </div>

      <TrustLogos className="z-20 mt-auto mb-[10rem] lg:-mb-[4rem] xl:-mb-[4rem] 2xl:mb-[4rem]" language={data?.settings?.language} />

      {/* Statue image */}
      {data?.heroImage && (
        <div
          className="pointer-events-none absolute bottom-[15rem] left-1/2 z-10 h-[42rem] w-[57rem] -translate-x-1/2 lg:bottom-[-14.75rem] lg:h-[94.625rem] lg:w-[97rem] lg:[clip-path:inset(0_0_19%_0)]"
          aria-hidden="true">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, ease, delay: 0.3 }}
            className="h-full w-full">
            <GroqImage
              src={data.heroImage}
              alt={data.heroImage.name || ""}
              className="object-contain object-top"
            />
          </motion.div>
        </div>
      )}

      {/* Blur bar — behind quotes, above statue (desktop only) */}
      <GradualBlur
        mode="fade"
        position="bottom"
        height="40rem"
        zIndex={15}
        style={{
          background: "linear-gradient(180deg, rgba(255,255,255,0) 0%, white 59.091%)",
        }}
        className=""
      />

      {/* Floating chips */}
      {/* {data?.heroFloatingChip1 && (
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, ease, delay: 0.7 }}
          className="absolute bottom-[60rem] left-[2rem] z-20 hidden items-center gap-[1rem] border border-[rgba(0,0,0,0.1)] bg-white p-[1rem] lg:bottom-[41.63rem] lg:left-[54rem] lg:flex lg:gap-[1.625rem] lg:p-[1.75rem]"
        >
          <CheckIcon />
          <span className="font-geist-mono text-[1.25rem] leading-[0.9] font-semibold whitespace-nowrap text-black uppercase lg:text-[1.625rem]">
            {data.heroFloatingChip1}
          </span>
        </motion.div>
      )}

      {data?.heroFloatingChip2 && (
        <motion.div
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, ease, delay: 0.8 }}
          className="absolute right-[2rem] bottom-[28rem] z-20 hidden items-center gap-[1rem] border border-[rgba(0,0,0,0.1)] bg-white p-[1rem] lg:right-[56.75rem] lg:bottom-[63.55rem] lg:flex lg:gap-[1.625rem] lg:p-[1.75rem]"
        >
          <CheckIcon />
          <span className="font-geist-mono text-[1.25rem] leading-[0.9] font-semibold whitespace-nowrap text-black uppercase lg:text-[1.625rem]">
            {data.heroFloatingChip2}
          </span>
        </motion.div>
      )} */}
    </section>
  );
}
